import React, { useEffect, useRef, useState } from "react";

interface Props {
  url: string;
}

const TIKTOK_OEMBED_BASE_URL = "https://www.tiktok.com/oembed";
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

const fetchWithRetry = async (
  input: string,
  attempt = 0,
): Promise<Response> => {
  try {
    return await fetch(input);
  } catch (error) {
    if (attempt < MAX_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      return fetchWithRetry(input, attempt + 1);
    }

    throw error;
  }
};

// Re-implementation of the (unmaintained) `react-tiktok` package's <TikTok /> component.
// TikTok's oEmbed API used to return a `<blockquote>` + `embed.js` <script> pair, which
// react-tiktok parsed by cutting the html at the first `<script>` tag. TikTok now returns
// a self-contained `<iframe>` with no script tag, which broke that parsing (silent blank
// embed). This mirrors react-tiktok's structure/behaviour 1:1, handling both response shapes.
export const TikTokEmbedPlayer = ({ url }: Props) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [scriptSrc, setScriptSrc] = useState<string | undefined>(undefined);
  const [html, setHtml] = useState<string | undefined>(undefined);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!("MutationObserver" in window)) {
      setLoaded(true);
      return undefined;
    }

    const elem = ref.current;

    const observer = new MutationObserver((mutationList) => {
      const iframeAdded = mutationList.reduce<HTMLIFrameElement | undefined>(
        (acc, curr) => {
          const iframe = Array.from(curr.addedNodes).find(
            (node) => node.nodeName === "IFRAME",
          ) as HTMLIFrameElement | undefined;

          return iframe ?? acc;
        },
        undefined,
      );

      if (iframeAdded) {
        iframeAdded.addEventListener("load", () => setLoaded(true));
      }
    });

    if (elem) {
      observer.observe(elem, {
        childList: true,
        attributes: true,
        subtree: true,
      });
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetchWithRetry(`${TIKTOK_OEMBED_BASE_URL}?url=${url}`)
      .then((res) => res.json())
      .then((res) => {
        if (res && res.status_msg) throw new Error(res.status_msg);
        if (!res || !res.html)
          throw new Error("API response doesn't look right");

        const htmlString: string = res.html;
        const tempElement = document.createElement("div");
        tempElement.innerHTML = htmlString;
        const scriptTag = tempElement.getElementsByTagName("script")[0];

        if (scriptTag) {
          // Legacy oEmbed shape: <blockquote>...</blockquote><script src="embed.js">
          setScriptSrc(scriptTag.src);
          setHtml(htmlString.substring(0, htmlString.indexOf("<script")));
        } else {
          // Current oEmbed shape: a self-contained <iframe>, no external script needed.
          // TikTok now marks this iframe `loading="lazy"`, which relies on an
          // IntersectionObserver that never fires while this component's wrapper div is
          // `display: none` (no layout box) — deadlocking the `loaded` state below forever.
          // Strip it so the iframe loads eagerly regardless of the wrapper's current display.
          const iframeTag = tempElement.getElementsByTagName("iframe")[0];
          iframeTag?.removeAttribute("loading");

          setScriptSrc(undefined);
          setHtml(tempElement.innerHTML);
        }
      })
      .catch((err) => setError(err));
  }, [url]);

  useEffect(() => {
    if (!scriptSrc) return undefined;

    const script = document.createElement("script");
    script.id = "ttEmbedder";
    script.async = true;
    script.src = scriptSrc;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [scriptSrc]);

  if (error) return <div>Error: {error.message}</div>;

  return (
    <div
      ref={ref}
      // "tiktok-embed" matches the class TikTok's old <blockquote> wrapper used to carry;
      // GalleryContentViewer.scss sizes this embed via that class, so we keep it even though
      // the oEmbed response no longer provides it itself.
      className="tiktok-embed"
      style={{ display: loaded && html ? "flex" : "none" }}
      dangerouslySetInnerHTML={{ __html: html || "" }}
    />
  );
};
